import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2Icon, Loader2Icon, MessageCircleIcon } from 'lucide-react';
import { CONTACT, PLANS, PROMO } from '../data/site';
type FormState = {
  parentName: string;
  playerName: string;
  age: string;
  level: 'Beginner' | 'Advanced' | '';
  phone: string;
  plan: string;
  notes: string;
};
const EMPTY_FORM: FormState = {
  parentName: '',
  playerName: '',
  age: '',
  level: '',
  phone: '',
  plan: 'Premium',
  notes: ''
};
const labelClass = 'block text-xs font-bold uppercase tracking-[0.14em] text-white/60';
const inputClass = 'mt-2 w-full rounded-xl border border-ink-line bg-ink px-4 py-3 text-sm text-white placeholder-white/35 outline-none transition-colors focus:border-brand-orange focus:ring-2 focus:ring-brand-orange/30';
function createRegistrationEmail(form: FormState) {
  const body = [`Parent name: ${form.parentName}`, `Player name: ${form.playerName}`, `Player age: ${form.age}`, `Level: ${form.level}`, `Phone: ${form.phone}`, `Membership interest: ${form.plan}`, form.notes ? `Notes: ${form.notes}` : '', '', `Promo: ${PROMO}`].filter(Boolean).join('\n');
  return `mailto:${CONTACT.email}?subject=${encodeURIComponent(`Leal 11 Sports registration — ${form.playerName || 'New player'}`)}&body=${encodeURIComponent(body)}`;
}
export function RegistrationForm() {
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const update = (key: keyof FormState, value: string) => setForm(prev => ({
    ...prev,
    [key]: value
  }));
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('submitting');
    const mailto = createRegistrationEmail(form);
    setTimeout(() => {
      window.location.href = mailto;
      setStatus('success');
    }, 700);
  };
  return <section id="register" className="w-full bg-ink py-16 sm:py-24" data-id="element-177" data-mp="2plbnqaw6rd8e">
      <div className="mx-auto max-w-6xl px-4 sm:px-6" data-id="element-178" data-mp="2pl3csojmihzu">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.1fr] lg:gap-14" data-id="element-179" data-mp="2ujmwsjenpjpk">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.3
        }} transition={{
          duration: 0.45
        }} data-id="element-180" data-mp="2zjj0ehjfz94l">
            <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-brand-orange" data-id="element-181" data-mp="26vmwarrjmexs">
              Registration
            </span>
            <h2 className="mt-3 text-3xl uppercase sm:text-4xl" data-id="element-182" data-mp="2bu6jswlsbys9">
              Claim your{' '}
              <span className="bg-gradient-to-r from-brand-orange to-brand-red bg-clip-text text-transparent" data-id="element-183" data-mp="2fu8pdgun5cct">
                20% discount
              </span>
            </h2>
            <p className="mt-4 text-white/70" data-id="element-184" data-mp="1o3kng2l582y3">
              Fill out the form and Coach Leonard will text you within 24 hours with your trial
              session time and group placement.
            </p>

            <ul className="mt-7 space-y-3 text-sm text-white/75" data-id="element-185" data-mp="1klwm9he87ucw">
              {['Locked-in 20% off before September 5th, 2025', 'Group placement by age and ability', 'Sessions at Juniper Valley Park, Middle Village'].map(item => <li key={item} className="flex gap-3" data-id="element-186" data-mp="16m5cjsuqt04p">
                  <CheckCircle2Icon className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange" aria-hidden="true" data-id="element-187" data-mp="11o3w7fopbewx" />
                  {item}
                </li>)}
            </ul>

            <a href={CONTACT.whatsapp} target="_blank" rel="noreferrer" className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-white/10" data-id="element-188" data-mp="ry0soljyeikc">
              <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-189" data-mp="e02p8b7686jm" />
              Prefer WhatsApp? Message us
            </a>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.45,
          delay: 0.08
        }} className="rounded-3xl border border-ink-line bg-ink-card p-6 sm:p-8" data-id="element-190" data-mp="2iyak3cg878y8">
            {status === 'success' ? <div className="flex h-full flex-col items-center justify-center py-10 text-center" data-id="element-191" data-mp="1zbcxn7rouksh">
                <span className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-brand-orange to-brand-red" data-id="element-192" data-mp="1lbjyqp60cy9f">
                  <CheckCircle2Icon className="h-7 w-7 text-white" aria-hidden="true" data-id="element-193" data-mp="1qbg34pimarpi" />
                </span>
                <h3 className="mt-5 font-display text-2xl uppercase" data-id="element-194" data-mp="3fq4fiqr7k9nj">Spot requested</h3>
                <p className="mt-3 max-w-sm text-sm text-white/70" data-id="element-195" data-mp="3kncz8vs08lqw">
                  Thanks {form.parentName || 'for reaching out'} — your registration is on its way.
                  Coach Leonard will text {form.phone || 'you'} within 24 hours.
                </p>
                <button type="button" onClick={() => {
              setForm(EMPTY_FORM);
              setStatus('idle');
            }} className="mt-6 text-sm font-semibold text-brand-orange underline underline-offset-4" data-id="element-196" data-mp="1irnmqam01p17">
                  Register another player
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-5" data-id="element-197" data-mp="21q9lvjxyh62h">
                <div className="grid gap-5 sm:grid-cols-2" data-id="element-198" data-mp="1wqwzt1ews30y">
                  <div data-id="element-199" data-mp="1wron7oycqug5">
                    <label className={labelClass} htmlFor="parentName" data-id="element-200" data-mp="1wp2iry6rcv25">
                      Parent name
                    </label>
                    <input id="parentName" name="parentName" required value={form.parentName} onChange={event => update('parentName', event.target.value)} className={inputClass} placeholder="Maria Rodriguez" data-id="element-201" data-mp="2dnp56ammbh4" />
                  </div>
                  <div data-id="element-202" data-mp="161u55i54ax9u">
                    <label className={labelClass} htmlFor="playerName" data-id="element-203" data-mp="162dngzyojnoe">
                      Player name
                    </label>
                    <input id="playerName" name="playerName" required value={form.playerName} onChange={event => update('playerName', event.target.value)} className={inputClass} placeholder="Diego Rodriguez" data-id="element-204" data-mp="1f1h9abp8eh6l" />
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2" data-id="element-205" data-mp="1yoop8dy0vums">
                  <div data-id="element-206" data-mp="1yn3qec4odpj4">
                    <label className={labelClass} htmlFor="age" data-id="element-207" data-mp="3j81wk6pgh90p">
                      Player age
                    </label>
                    <input id="age" name="age" type="number" min={4} max={18} required value={form.age} onChange={event => update('age', event.target.value)} className={inputClass} placeholder="11" data-id="element-208" data-mp="3e7ea7yc69gct" />
                  </div>
                  <div data-id="element-209" data-mp="3s7xecudio29p">
                    <label className={labelClass} htmlFor="level" data-id="element-210" data-mp="3s75f99kpnmeo">
                      Level
                    </label>
                    <select id="level" name="level" required value={form.level} onChange={event => update('level', event.target.value)} className={inputClass} data-id="element-211" data-mp="3n8kpk4334l4h">
                      <option value="" disabled data-id="element-212" data-mp="3o83qwd0gb2f4">
                        Select level
                      </option>
                      <option value="Beginner" data-id="element-213" data-mp="3f9id0v5xcbko">Beginner</option>
                      <option value="Advanced" data-id="element-214" data-mp="3fb39wrfpo6lp">Advanced</option>
                    </select>
                  </div>
                </div>

                <div className="grid gap-5 sm:grid-cols-2" data-id="element-215" data-mp="bstg80w58g87">
                  <div data-id="element-216" data-mp="btb86lbrw8ab">
                    <label className={labelClass} htmlFor="phone" data-id="element-217" data-mp="bqytqi584g20">
                      Phone
                    </label>
                    <input id="phone" name="phone" type="tel" required value={form.phone} onChange={event => update('phone', event.target.value)} className={inputClass} placeholder="(646) 555-0134" data-id="element-218" data-mp="1uvrlcadghf2" />
                  </div>
                  <div data-id="element-219" data-mp="2jzn6w6sv6f0x">
                    <label className={labelClass} htmlFor="plan" data-id="element-220" data-mp="2jyv9cqfpi780">
                      Membership interest
                    </label>
                    <select id="plan" name="plan" value={form.plan} onChange={event => update('plan', event.target.value)} className={inputClass} data-id="element-221" data-mp="2t14k9hew02lx">
                      {PLANS.map(plan => <option key={plan.name} value={plan.name} data-id="element-222" data-mp="1wbdntcn1c5dh">
                          {plan.name} — ${plan.price}/mo
                        </option>)}
                      <option value="Not sure yet" data-id="element-223" data-mp="25bsdeo7186ms">Not sure yet</option>
                    </select>
                  </div>
                </div>

                <div data-id="element-224" data-mp="1hkmws6t9pabf">
                  <label className={labelClass} htmlFor="notes" data-id="element-225" data-mp="1hjv1kx3l1elo">
                    Anything we should know? (optional)
                  </label>
                  <textarea id="notes" name="notes" rows={3} value={form.notes} onChange={event => update('notes', event.target.value)} className={inputClass} placeholder="Preferred days, position, previous club experience..." data-id="element-226" data-mp="ic122hzanrl8" />
                </div>

                <button type="submit" disabled={status === 'submitting'} className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-gradient-to-r from-brand-orange to-brand-red px-7 py-4 text-sm font-bold uppercase tracking-wide text-white shadow-xl shadow-brand-red/25 transition-transform hover:scale-[1.02] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-227" data-mp="3vj2vw3y2t8kq">
                  {status === 'submitting' ? <>
                      <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-228" data-mp="37syqbr1buyr3" />
                      Sending…
                    </> : 'Claim 20% Discount Now'}
                </button>
                <p className="text-center text-xs text-white/45" data-id="element-229" data-mp="1waku9vmkpzic">
                  No payment today. We confirm placement by text before anything is charged.
                </p>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}